import { useTranslation } from 'react-i18next';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Tab,
  TableContainer,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Popover,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { toast } from 'material-react-toastify';
import { cloneDeep, isEmpty, isFinite } from 'lodash';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
} from '@mui/x-data-grid';
import { DateTime } from 'luxon';
import { useRecoilValue } from 'recoil';
import { toastApiError } from '../../utils/ApiError';
import Waiting from '../Waiting';
import { sharedColors, sharedStyles } from '../../utils/Style';
import {
  Schedule,
  ScheduleStats,
  ScheduleStatus,
  ScheduleSummary,
} from '../../interfaces/Schedule';
import {
  calculateScheduleStats,
  combineSchedules,
  deleteSchedule,
  updateSchedule,
} from '../../services/Schedule';
import {
  formatSingleDay,
  getAllDatesBetweenTwoDate,
  isHoliday,
  isWorkday,
} from '../../utils/Date';
import {
  compareSchedulePersonnelNames,
  getScheduleTemplate,
  getWorkedHoursByDate,
  hoursRangeStrFromShift,
  hoursStringFromShift,
} from '../../utils/Schedule';
import ConfirmDialog from '../ConfirmDialog';
import { FullDayDates } from '../../interfaces/FullDayDates';
import DepartmentSettingsTooltip from '../Department/DepartmentSettingsTooltip';
import { tokenDataAtom } from '../../atoms/TokenData';
import { hasWriteAccess } from '../../utils/TokenData';
import { AccessLevel } from '../../interfaces/User';
import RestoreScheduleDialog from './RestoreScheduleDialog';
import FullDayOptionsTooltip from './FullDayOptionsTooltip';
import { PtoSubtype } from '../../interfaces/PtoSubtype';
import AddScheduleNotesDialog from './AddScheduleNotesDialog';
import { Shift } from '../../interfaces/Shift';
import ScheduleNotesDialog from './ScheduleNotesDialog';
import ScheduleApprovalCard from './ScheduleApprovalCard';
import ScheduleApprovalRollbackCard from './ScheduleApprovalRollbackCard';
import { PersonnelCountsByDay } from '../../interfaces/PersonnelCounts';
import {
  getMinimumPersonnelCountsByDay,
  getWorkingPersonnelCountsByDay,
} from '../../utils/PersonnelCounts';
import { RotationType } from '../../utils/enums/RotationType';
import ScheduleGridCell from './ScheduleGridCell';
import {
  getFullDayMultiplierLabelFromValue,
  getOvertimeMultiplierLabelFromValue,
} from '../../interfaces/AlgorithmParameterMultipliers';
import { ScheduleEndEarlyDialog } from './ScheduleEndEarlyDialog';
import { ScheduleCombineDialog } from './ScheduleCombineDialog';
import { ReserveList } from './ReserveList';
import MobileSchedulePlan from './MobileSchedulePlan';
import ScheduleStatusField from './ScheduleStatusField';
import ScheduleSurface from './ScheduleSurface';
import TapTooltip from '../TapTooltip';

type SelectedTab = 'plan' | 'reserve' | 'details';

interface UpdateScheduleDialogProps {
  clickedSchedule?: Schedule;
  mobilePage?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  scheduleSummaries: ScheduleSummary[];
  onClose: () => void;
  onModify: () => void;
}

const getStatsRowFields = (stats: ScheduleStats): Record<string, number> => ({
  totalWorkedHours: stats.totalWorkedHours,
  totalHoursShouldWorked: stats.totalHoursShouldWorked,
  totalOvertimeHours: stats.totalOvertimeHours,
  total_increased_normal_overtime_hours:
    stats.scheduleOvertimeHours.increasedNormalOvertimeHours,
  total_non_increased_normal_overtime_hours:
    stats.scheduleOvertimeHours.nonIncreasedNormalOvertimeHours,
  total_increased_holiday_overtime_hours:
    stats.scheduleOvertimeHours.increasedHolidayOvertimeHours,
  total_non_increased_holiday_overtime_hours:
    stats.scheduleOvertimeHours.nonIncreasedHolidayOvertimeHours,
});

const UpdateScheduleDialog = (props: UpdateScheduleDialogProps) => {
  const { t } = useTranslation();
  const mobile = !!props.mobilePage;
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);

  const tokenData = useRecoilValue(tokenDataAtom);

  const [loading, setLoading] = useState(false);
  const [currentState, setCurrentState] = useState<Schedule>(
    props.clickedSchedule ?? getScheduleTemplate(),
  );
  const [selectedTab, setSelectedTab] = useState<SelectedTab>('plan');
  const [statsLoading, setStatsLoading] = useState(false);
  const [minPersonnelCounts, setMinPersonnelCounts] =
    useState<PersonnelCountsByDay>({});
  const [clickedDelete, setClickedDelete] = useState(false);
  const [clickedUpdate, setClickedUpdate] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showEndEarlyDialog, setShowEndEarlyDialog] = useState(false);
  const [showCombineScheduleDialog, setShowCombineScheduleDialog] =
    useState(false);
  const statsRequestID = useRef(0);

  const dirty =
    !!props.clickedSchedule &&
    ['name', 'plan', 'overtimeHours', 'ptoSubtypes', 'reserveList'].some(
      (field) =>
        JSON.stringify(currentState[field as keyof Schedule]) !==
        JSON.stringify(props.clickedSchedule?.[field as keyof Schedule]),
    );
  useEffect(() => {
    props.onDirtyChange?.(dirty);
  }, [dirty, props.onDirtyChange]);

  const readOnly =
    !hasWriteAccess(tokenData, AccessLevel.DEPARTMENT_SECTION) ||
    currentState.status === ScheduleStatus.APPROVED;

  useEffect(() => {
    setCurrentState(props.clickedSchedule ?? getScheduleTemplate());
    setMinPersonnelCounts(
      props.clickedSchedule
        ? getMinimumPersonnelCountsByDay(props.clickedSchedule)
        : {},
    );
    setShowRestoreDialog(false);
  }, [props.clickedSchedule]);

  useEffect(() => {
    let cleanupObserver: (() => void) | undefined;
    const timeout = setTimeout(() => {
      const headerInner = document.querySelector(
        '.MuiDataGrid-columnHeadersInner',
      );
      const firstHeader = document.querySelector(
        '.MuiDataGrid-columnHeader[data-field="name"]',
      );

      if (!headerInner || !firstHeader) return;

      const observer = new MutationObserver(() => {
        // @ts-ignore
        const match = headerInner.style.transform.match(
          /translate3d\((-?\d+)px, 0px, 0px\)/,
        );
        if (match) {
          const offsetX = parseInt(match[1], 10);
          // @ts-ignore
          firstHeader.style.transform = `translateX(${-offsetX}px)`;
        }
      });

      observer.observe(headerInner, {
        attributes: true,
        attributeFilter: ['style'],
      });

      cleanupObserver = () => observer.disconnect();
    }, 300);

    return () => {
      clearTimeout(timeout);
      cleanupObserver?.();
    };
  }, [selectedTab]);

  const handleChangeName = (e: any) => {
    setCurrentState({ ...currentState, name: e.target.value });
  };

  const handleUpdate = (notes: string, files: File[]) => {
    setClickedUpdate(false);
    setLoading(true);

    updateSchedule(currentState, notes, files)
      .then(() => {
        toast.success(t('schedule.successful.update'));
        props.onModify();
      })
      .catch((err) => toastApiError(err))
      .finally(() => setLoading(false));
  };

  const handleEndEarly = (endDate: Date, notes: string) => {
    setShowEndEarlyDialog(false);
    setLoading(true);

    const endDateString = endDate.toISOString();

    const plan = cloneDeep(currentState.plan);
    const overtimeHours = cloneDeep(currentState.overtimeHours);
    const ptoSubtypes = cloneDeep(currentState.ptoSubtypes);

    Object.entries(currentState.plan).forEach(([personnel, day]) => {
      Object.entries(day).forEach(([dayDate, _]) => {
        if (dayDate > endDateString) {
          delete plan[personnel][dayDate];
        }
      });
    });

    Object.entries(currentState.overtimeHours).forEach(([personnel, day]) => {
      Object.entries(day).forEach(([dayDate, _]) => {
        if (dayDate > endDateString) {
          delete overtimeHours[personnel][dayDate];
        }
      });
    });

    Object.entries(currentState.ptoSubtypes).forEach(([personnel, day]) => {
      Object.entries(day).forEach(([dayDate, _]) => {
        if (dayDate > endDateString) {
          delete ptoSubtypes[personnel][dayDate];
        }
      });
    });

    updateSchedule(
      {
        ...props.clickedSchedule!,
        plan,
        overtimeHours,
        ptoSubtypes,
        endAt: endDate,
      },
      notes,
      [],
    )
      .then(() => {
        toast.success(t('schedule.successful.update'));
        props.onModify();
      })
      .catch((err) => toastApiError(err))
      .finally(() => setLoading(false));
  };

  const handleCombineSchedules = (
    id: number,
    keepOldPlans: boolean,
    name: string,
  ) => {
    setShowCombineScheduleDialog(false);
    setLoading(true);

    combineSchedules(id, currentState.ID!, keepOldPlans, name)
      .then(() => {
        toast.success('Planlar birleştirildi');
        props.onModify();
      })
      .catch((err) => toastApiError(err))
      .finally(() => setLoading(false));
  };

  const handleClickUpdate = () => {
    if (
      JSON.stringify(currentState.plan) !==
        JSON.stringify(props.clickedSchedule?.plan ?? {}) ||
      JSON.stringify(currentState.overtimeHours) !==
        JSON.stringify(props.clickedSchedule?.overtimeHours ?? {}) ||
      JSON.stringify(currentState.ptoSubtypes) !==
        JSON.stringify(props.clickedSchedule?.ptoSubtypes ?? {}) ||
      JSON.stringify(currentState.reserveList) !==
        JSON.stringify(props.clickedSchedule?.reserveList ?? {})
    ) {
      setClickedUpdate(true);
    } else {
      handleUpdate('', []);
    }
  };

  const handleConfirmDelete = () => {
    if (!isFinite(props.clickedSchedule?.ID)) {
      return;
    }

    setLoading(true);

    deleteSchedule(props.clickedSchedule!.ID!)
      .then(() => {
        toast.success(t('schedule.successful.delete'));
        setClickedDelete(false);
        props.onModify();
      })
      .catch((err) => toastApiError(err))
      .finally(() => setLoading(false));
  };

  const refreshScheduleStats = useCallback((schedule: Schedule) => {
    const requestID = statsRequestID.current + 1;
    statsRequestID.current = requestID;
    setStatsLoading(true);

    calculateScheduleStats(schedule)
      .then((stats) => {
        if (statsRequestID.current !== requestID) return;

        setCurrentState((prevState) => ({
          ...prevState,
          totalStats: stats.totalStats,
          personnelStats: stats.personnelStats,
        }));
      })
      .catch((err) => {
        if (statsRequestID.current === requestID) {
          toastApiError(err);
        }
      })
      .finally(() => {
        if (statsRequestID.current === requestID) {
          setStatsLoading(false);
        }
      });
  }, []);

  const handleOvertimeChange = useCallback(
    (personnelName: string, dateStr: string, increment: number) => {
      const personnelOvertimeHours = {
        ...(currentState.overtimeHours[personnelName] ?? {}),
      };
      personnelOvertimeHours[dateStr] =
        (personnelOvertimeHours[dateStr] ?? 0) + increment;

      const nextState = {
        ...currentState,
        overtimeHours: {
          ...currentState.overtimeHours,
          [personnelName]: personnelOvertimeHours,
        },
      };

      setCurrentState(nextState);
      refreshScheduleStats(nextState);
    },
    [currentState, refreshScheduleStats],
  );

  const dates = useMemo(
    () => getAllDatesBetweenTwoDate(currentState.startAt, currentState.endAt),
    [currentState.endAt, currentState.startAt],
  );

  const isExpired = useMemo(
    () =>
      props.clickedSchedule
        ? DateTime.now().minus({ day: 1 }).toJSDate() >
          props.clickedSchedule.endAt
        : true,
    [props.clickedSchedule],
  );

  const getColumnColorForDay = useCallback(
    (day: Date): string => {
      if (!isWorkday(day)) return 'gray3';

      return isHoliday(day, currentState.fullDayDates) ? 'gray4' : 'gray1';
    },
    [currentState.fullDayDates],
  );

  const hasAdministrativeHours = currentState.administrativeHours !== 0;
  const workingPersonnelCounts = useMemo(
    () => getWorkingPersonnelCountsByDay(currentState),
    [currentState],
  );

  const datesColumns: GridColDef[] = useMemo(
    () =>
      dates.map((dateDay) => {
        const dateStr = DateTime.fromJSDate(dateDay).toISODate()!;

        const isDayWorkday = isWorkday(dateDay);

        const minPersonnels = minPersonnelCounts[dateStr] ?? {
          day: 0,
          night: 0,
        };
        const workingPersonnels = workingPersonnelCounts[dateStr] ?? {
          day: 0,
          night: 0,
        };
        const personnelCountWarnings: string[] = [];
        if (
          workingPersonnels.day < minPersonnels.day ||
          (isDayWorkday && workingPersonnels.day > minPersonnels.day + 1) ||
          (!isDayWorkday && workingPersonnels.day > minPersonnels.day)
        ) {
          personnelCountWarnings.push(
            t('schedule.warning.personnel_count', {
              shift: t('schedule.shifts.DAY'),
              expected: isDayWorkday
                ? minPersonnels.day + 1
                : minPersonnels.day,
              actual: workingPersonnels.day,
            }),
          );
        }
        if (workingPersonnels.night !== minPersonnels.night) {
          personnelCountWarnings.push(
            t('schedule.warning.personnel_count', {
              shift: t('schedule.shifts.NIGHT'),
              expected: minPersonnels.night,
              actual: workingPersonnels.night,
            }),
          );
        }

        return {
          field: dateStr,
          headerName: dateDay.toLocaleDateString('en-GB'),
          flex: 1,
          editable: !readOnly,
          minWidth: 100,
          sortable: false,
          type: 'singleSelect',
          valueOptions: (params: GridRenderCellParams) => [
            ...[
              Shift.EMPTY,
              Shift.DAY,
              Shift.NIGHT,
              Shift.ALL_DAY,
              Shift.EIGHT_HOUR_NIGHT,
              Shift.SIXTEEN_HOUR_DAY,
              Shift.SIXTEEN_TWENTY,
              Shift.EIGHT_TWENTY,
            ].map((shift) => {
              const is4d =
                props.clickedSchedule?.fourDPersonnelNames.includes(
                  params.id as string,
                ) ?? false;

              const dayShiftHour = props.clickedSchedule?.shiftHours[
                params.id as string
              ]
                ? props.clickedSchedule?.shiftHours[params.id as string].DAY
                : 9;

              const hoursRange = hoursRangeStrFromShift(
                shift,
                is4d,
                dayShiftHour,
              );
              const hoursRangeStr = hoursRange ? `[${hoursRange}]` : '';

              return {
                value: shift,
                label: `${hoursStringFromShift(
                  currentState,
                  is4d,
                  params.id as string,
                  undefined,
                  shift,
                )} ${hoursRangeStr}`,
              };
            }),
            ...Object.keys(PtoSubtype).map((ptoSubtype) => ({
              value: ptoSubtype,
              label: t(`pto_subtype.name.${ptoSubtype}`),
            })),
          ],
          renderCell: (params: GridRenderCellParams) => (
            <ScheduleGridCell
              dateDay={dateDay}
              dateStr={dateStr}
              schedule={currentState}
              personnelCountWarnings={personnelCountWarnings}
              params={params}
              editable={!readOnly && !isExpired}
              is4d={
                props.clickedSchedule?.fourDPersonnelNames.includes(
                  params.id as string,
                ) ?? false
              }
              onOvertimeHoursChange={(increment) =>
                handleOvertimeChange(params.id as string, dateStr, increment)
              }
            />
          ),
          cellClassName: () => getColumnColorForDay(dateDay),
          headerClassName: () => getColumnColorForDay(dateDay),
        };
      }),
    [
      currentState,
      dates,
      getColumnColorForDay,
      handleOvertimeChange,
      isExpired,
      minPersonnelCounts,
      props.clickedSchedule?.fourDPersonnelNames,
      props.clickedSchedule?.shiftHours,
      readOnly,
      t,
      workingPersonnelCounts,
    ],
  );

  const renderStatsCell = useCallback(
    (params: GridRenderCellParams) => (
      <Box
        component='div'
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box component='span' sx={{ opacity: statsLoading ? 0.45 : 1 }}>
          {params.value ?? ''}
        </Box>
        {statsLoading && (
          <Box
            component='div'
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.65)',
            }}
          >
            <CircularProgress size={18} />
          </Box>
        )}
      </Box>
    ),
    [statsLoading],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('personnel.name')!,
        flex: 1,
        editable: false,
        sortable: false,
        minWidth: 170,
        cellClassName: () => 'sticky-column',
      },
      {
        field: 'status',
        headerName: 'Statü',
        flex: 1,
        editable: false,
        sortable: false,
        minWidth: 90,
      },
      ...datesColumns,
      ...(hasAdministrativeHours
        ? [
            {
              field: 'administrativeHours',
              headerName: t('schedule.administrative_hours') as string,
              flex: 1,
              editable: false,
              sortable: false,
              minWidth: 150,
            },
          ]
        : []),
      {
        field: 'totalWorkedHours',
        headerName: t('schedule.total_hours') as string,
        flex: 1,
        editable: false,
        sortable: false,
        minWidth: 150,
        renderCell: renderStatsCell,
      },
      {
        field: 'totalHoursShouldWorked',
        headerName: t('schedule.total_hours_should_worked') as string,
        flex: 1,
        editable: false,
        sortable: false,
        minWidth: 200,
        renderCell: renderStatsCell,
      },
      {
        field: 'totalOvertimeHours',
        headerName: t('schedule.overtime_hours') as string,
        flex: 1,
        editable: false,
        sortable: false,
        minWidth: 150,
        renderCell: renderStatsCell,
      },
      {
        field: 'total_increased_normal_overtime_hours',
        headerName: 'Normal Artırımlı',
        flex: 1,
        editable: false,
        sortable: false,
        minWidth: 160,
        renderCell: renderStatsCell,
      },
      {
        field: 'total_non_increased_normal_overtime_hours',
        headerName: 'Normal Artırımsız',
        flex: 1,
        editable: false,
        sortable: false,
        minWidth: 170,
        renderCell: renderStatsCell,
      },
      {
        field: 'total_increased_holiday_overtime_hours',
        headerName: 'Bayram Artırımlı',
        flex: 1,
        editable: false,
        sortable: false,
        minWidth: 160,
        renderCell: renderStatsCell,
      },
      {
        field: 'total_non_increased_holiday_overtime_hours',
        headerName: 'Bayram Artırımsız',
        flex: 1,
        editable: false,
        sortable: false,
        minWidth: 170,
        renderCell: renderStatsCell,
      },
    ],
    [datesColumns, hasAdministrativeHours, renderStatsCell, t],
  );

  const getTotalHoursRow = useCallback(() => {
    const totalHours: Record<string, number> =
      getWorkedHoursByDate(currentState);

    const row: Record<string, string | number> = {
      id: t('shared.total') as string,
      name: t('shared.total') as string,
      administrativeHours:
        currentState.administrativeHours *
        Object.keys(currentState.plan).length,
      ...getStatsRowFields(currentState.totalStats),
    };
    Object.keys(totalHours).forEach((date: string) => {
      row[date] = totalHours[date];
    });
    return row;
  }, [currentState, t]);

  const onRowEditCommit = useCallback(
    (newRow: any, oldRow: any) => {
      let changedDate: string = '';
      Object.keys(newRow).forEach((key: string) => {
        if (newRow[key] !== oldRow[key]) {
          changedDate = key;
          return true;
        }
        return false;
      });

      const plan = cloneDeep(currentState.plan);
      const overtimeHours = cloneDeep(currentState.overtimeHours);
      const ptoSubtypes = cloneDeep(currentState.ptoSubtypes);
      if (!ptoSubtypes[newRow.name]) {
        ptoSubtypes[newRow.name] = {};
      }

      const shift = newRow[changedDate];
      if (shift === Shift.EMPTY) {
        delete plan[newRow.name][changedDate];
        delete ptoSubtypes[newRow.name][changedDate];
      } else if (shift === PtoSubtype.SPECIAL_REQUEST) {
        plan[newRow.name][changedDate] = Shift.ETO;
        ptoSubtypes[newRow.name][changedDate] = PtoSubtype.SPECIAL_REQUEST;
      } else if (Object.keys(PtoSubtype).includes(shift)) {
        plan[newRow.name][changedDate] = Shift.PTO;
        ptoSubtypes[newRow.name][changedDate] = shift;
      } else {
        plan[newRow.name][changedDate] = shift;
      }

      const personnelOvertimeHours = overtimeHours[newRow.name] ?? {};
      personnelOvertimeHours[changedDate] = 0;
      overtimeHours[newRow.name] = personnelOvertimeHours;

      const nextState = {
        ...currentState,
        plan,
        overtimeHours,
        ptoSubtypes,
      };

      setCurrentState(nextState);
      refreshScheduleStats(nextState);
      return newRow;
    },
    [currentState, refreshScheduleStats],
  );

  const rows: GridRowsProp = useMemo(() => {
    const personnelRows = Object.keys(currentState.plan)
      .sort((n1, n2) => compareSchedulePersonnelNames(n1, n2, currentState))
      .map((name) => {
        const personnelPlan = currentState.plan[name];
        const stats = currentState.personnelStats[name];

        const row: Record<string, string | number> = {
          id: name,
          status: props.clickedSchedule?.fourDPersonnelNames.includes(name)
            ? '4D'
            : 'Normal',
          name,
          administrativeHours: currentState.administrativeHours,
          ...(stats ? getStatsRowFields(stats) : {}),
        };

        dates.forEach((date: Date) => {
          const dateStr = DateTime.fromJSDate(date).toISODate()!;

          let shift: string = personnelPlan[dateStr];
          if (shift === Shift.PTO || shift === Shift.ETO) {
            const personnelSubtypes = currentState.ptoSubtypes[name] ?? {};
            shift =
              personnelSubtypes[dateStr] ??
              (shift === Shift.PTO
                ? PtoSubtype.ANNUAL
                : PtoSubtype.SPECIAL_REQUEST);
          }

          row[dateStr] = shift;
        });

        return row;
      });

    return [...personnelRows, getTotalHoursRow()];
  }, [
    currentState,
    dates,
    getTotalHoursRow,
    props.clickedSchedule?.fourDPersonnelNames,
  ]);

  return (
    <ScheduleSurface
      mobile={mobile}
      open={!!props.clickedSchedule}
      onClose={props.onClose}
    >
      {isFinite(currentState.ID) && (
        <RestoreScheduleDialog
          scheduleID={currentState.ID!}
          open={showRestoreDialog}
          onRollback={props.onModify}
          onClose={() => setShowRestoreDialog(false)}
        />
      )}
      {showEndEarlyDialog && (
        <ScheduleEndEarlyDialog
          startAt={currentState.startAt}
          endAt={currentState.endAt}
          onCancel={() => setShowEndEarlyDialog(false)}
          onClose={(d: Date, n: string) => handleEndEarly(d, n)}
        />
      )}
      {showCombineScheduleDialog && (
        <ScheduleCombineDialog
          scheduleSummaries={props.scheduleSummaries}
          schedule={currentState}
          onCancel={() => setShowCombineScheduleDialog(false)}
          onClose={(id: number, b: boolean, n: string) =>
            handleCombineSchedules(id, b, n)
          }
        />
      )}
      <ScheduleNotesDialog
        open={showNotesDialog}
        scheduleID={currentState.ID ?? -1}
        onComment={() =>
          setCurrentState({
            ...currentState,
            scheduleNotesCount: currentState.scheduleNotesCount + 1,
          })
        }
        onClose={() => setShowNotesDialog(false)}
      />
      <AddScheduleNotesDialog
        title={t('schedule.update_notes.title') as string}
        content={t('schedule.update_notes.description') as string}
        open={clickedUpdate}
        onClose={() => setClickedUpdate(false)}
        onConfirm={handleUpdate}
        required={true}
      />
      <ConfirmDialog
        content={t('schedule.delete.confirm_message')}
        open={clickedDelete}
        onClose={() => setClickedDelete(false)}
        onConfirm={handleConfirmDelete}
        color='error'
      />
      <Waiting open={loading} />
      {mobile ? (
        <Box sx={{ px: 1, py: 1 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}
          >
            <IconButton
              onClick={props.onClose}
              aria-label={String(t('schedule.mobile.back'))}
              sx={{ flexShrink: 0 }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              component='h1'
              variant='h6'
              noWrap
              title={currentState.name}
              sx={{ flex: 1, minWidth: 0, color: sharedColors.gray6 }}
            >
              {currentState.name}
            </Typography>
            <Box sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
              <ScheduleStatusField scheduleStatus={currentState.status} />
            </Box>
          </Box>
          <Typography variant='body2' sx={{ mt: 0.5 }}>
            {formatSingleDay(t, currentState.startAt)} –{' '}
            {formatSingleDay(t, currentState.endAt)}
          </Typography>
        </Box>
      ) : (
        <DialogTitle sx={{ ...sharedStyles.h6, color: sharedColors.gray6 }}>
          {t('schedule.dialog.update')}
        </DialogTitle>
      )}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          ...(mobile ? { p: 1, overflow: 'visible' } : {}),
        }}
      >
        {mobile && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button onClick={() => setShowNotesDialog(true)}>
                {t('schedule.update_notes.notes_button', {
                  n: currentState.scheduleNotesCount.toString(),
                })}
              </Button>
              <ScheduleApprovalCard
                schedule={currentState}
                onChange={props.onModify}
              />
              <ScheduleApprovalRollbackCard
                schedule={currentState}
                onChange={props.onModify}
              />
            </Box>
            <Tabs
              value={selectedTab}
              onChange={(_, value) => setSelectedTab(value)}
              variant='fullWidth'
            >
              <Tab value='plan' label={t('schedule.dialog.tab.plan')} />
              <Tab value='reserve' label={t('schedule.mobile.reserve')} />
              <Tab value='details' label={t('schedule.mobile.details')} />
            </Tabs>
          </>
        )}
        <Box
          sx={{
            display: mobile && selectedTab !== 'details' ? 'none' : 'block',
            ...(mobile
              ? {
                  '& > .MuiBox-root': { flexWrap: 'wrap', gap: 1.5 },
                  '& .MuiTextField-root': { maxWidth: '100%', mr: 0 },
                }
              : {}),
          }}
        >
          <Box
            component='div'
            sx={{ display: 'flex', flexDirection: 'row', mt: 1.5 }}
          >
            <TextField
              label={t('schedule.name')}
              value={currentState.name}
              disabled={readOnly}
              onChange={handleChangeName}
              size='small'
              error={isEmpty(currentState.name)}
              sx={{ width: mobile ? '100%' : 200, mr: 1.5 }}
            />
            <Tooltip title={currentState.institutionName}>
              <TextField
                label={t('schedule.institution')}
                value={currentState.institutionName}
                disabled={true}
                size='small'
                sx={{ width: mobile ? '100%' : 200, mr: 1.5 }}
              />
            </Tooltip>
            <TextField
              label={t('schedule.department')}
              value={currentState.department.name}
              disabled={true}
              size='small'
              sx={{ width: mobile ? '100%' : 200, mr: 1.5 }}
            />
            <TextField
              label={t('schedule.department_section')}
              value={currentState.departmentSection.name}
              disabled={true}
              size='small'
              sx={{ width: mobile ? '100%' : 200 }}
            />
          </Box>
          <Box
            component='div'
            sx={{ display: 'flex', flexDirection: 'row', mt: 1.5 }}
          >
            <TextField
              label={t('shared.start_at')}
              value={formatSingleDay(t, currentState.startAt)}
              disabled={true}
              size='small'
              sx={{ width: 160, mr: 1.5 }}
            />
            <TextField
              label={t('shared.end_at')}
              value={formatSingleDay(t, currentState.endAt)}
              disabled={true}
              size='small'
              sx={{ width: 160, mr: 1.5 }}
            />
            <TextField
              label={t('schedule.weekday_rotation_type')}
              value={
                currentState.weekdayRotationType === RotationType.FULL_DAY
                  ? t('schedule.all_day_rotation')
                  : t('schedule.day_night_rotation')
              }
              disabled={true}
              size='small'
              sx={{ width: 160, mr: 1.5 }}
            />
            <TextField
              label={t('schedule.weekend_rotation_type')}
              value={
                currentState.weekendRotationType === RotationType.FULL_DAY
                  ? t('schedule.all_day_rotation')
                  : t('schedule.day_night_rotation')
              }
              disabled={true}
              size='small'
              sx={{ width: 160, mr: 1.5 }}
            />
            <TextField
              label={t('schedule.previous_schedule')}
              value={
                currentState.previousScheduleName
                  ? currentState.previousScheduleName
                  : t('shared.not_exist')
              }
              disabled={true}
              size='small'
              sx={{ width: 160, mr: 1.5 }}
            />
            <Button
              color='primary'
              variant='outlined'
              onClick={() => setShowNotesDialog(true)}
              sx={{ ...sharedStyles.buttonText, mr: 1.5 }}
            >
              {t('schedule.update_notes.notes_button', {
                n: currentState.scheduleNotesCount.toString(),
              })}
            </Button>
            <DepartmentSettingsTooltip
              mobile={mobile}
              departmentSettings={currentState.departmentSettings}
            >
              <Button
                color='primary'
                endIcon={<InfoIcon />}
                sx={{
                  ...sharedStyles.buttonText,
                  textAlign: 'left',
                  justifyContent: 'left',
                  p: 0,
                }}
              >
                {t('schedule.personnel_constraints')}
              </Button>
            </DepartmentSettingsTooltip>
            <TapTooltip
              mobile={mobile}
              title={
                <Typography>
                  {t('schedule.overtime_hours_equality')}:
                  {getOvertimeMultiplierLabelFromValue(
                    currentState.algorithmParameterMultipliers.overtimeHours,
                  )}
                  <br />
                  {t('schedule.non_fullday_night_punish')}:
                  {getFullDayMultiplierLabelFromValue(
                    currentState.algorithmParameterMultipliers.nonFullDayNight,
                  )}
                  <br />
                  {t('schedule.fullday_night_reward')}:
                  {getFullDayMultiplierLabelFromValue(
                    currentState.algorithmParameterMultipliers.fullDayNight,
                  )}
                  <br />
                </Typography>
              }
            >
              <Button
                color='primary'
                endIcon={<InfoIcon />}
                sx={{
                  ...sharedStyles.buttonText,
                  textAlign: 'left',
                  justifyContent: 'left',
                  p: 0,
                  ml: 2,
                }}
              >
                {t('schedule.advanced_settings')}
              </Button>
            </TapTooltip>
          </Box>
          {currentState.fullDayDates.length > 0 && (
            <Box
              component='div'
              sx={{ display: 'flex', flexDirection: 'column', mt: 1.5 }}
            >
              <Typography sx={sharedStyles.h7}>
                {t('schedule.all_day_dates')}
              </Typography>

              {currentState.fullDayDates.map((fullDays: FullDayDates) => (
                <Box
                  component='div'
                  sx={{ display: 'flex', flexDirection: 'row', mt: 1.5 }}
                >
                  <TextField
                    label={t('shared.start_at')}
                    value={formatSingleDay(t, fullDays.startAt)}
                    disabled={true}
                    size='small'
                    sx={{ width: 200, mr: 1.5 }}
                  />
                  <TextField
                    label={t('shared.end_at')}
                    value={formatSingleDay(t, fullDays.endAt)}
                    disabled={true}
                    size='small'
                    sx={{ width: 200, mr: 1.5 }}
                  />
                  <TextField
                    label={t('schedule.name')}
                    value={fullDays.name}
                    disabled={true}
                    size='small'
                    sx={{ width: 200, mr: 1.5 }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size='small'
                        disabled={true}
                        checked={fullDays.isOfficialHoliday}
                        color='primary'
                        inputProps={{ 'aria-label': 'primary checkbox' }}
                        sx={{ mr: 1.5 }}
                      />
                    }
                    label={
                      <Typography sx={{ ...sharedStyles.subtitle2 }}>
                        {t('schedule.is_official_holiday')}
                      </Typography>
                    }
                  />
                  <TextField
                    label={t('schedule.rotation_type')}
                    value={
                      fullDays.rotationType === RotationType.FULL_DAY
                        ? t('schedule.all_day_rotation')
                        : t('schedule.day_night_rotation')
                    }
                    disabled={true}
                    size='small'
                    sx={{ width: 160, mr: 1.5 }}
                  />
                  <FullDayOptionsTooltip
                    mobile={mobile}
                    personnels={currentState.departmentSection.personnels}
                    fullDay={fullDays}
                  >
                    <Button
                      color='primary'
                      endIcon={<InfoIcon />}
                      sx={{
                        ...sharedStyles.buttonText,
                        textAlign: 'left',
                        justifyContent: 'left',
                        p: 0,
                      }}
                    >
                      {t('schedule.all_day_personnel_constraints')}
                    </Button>
                  </FullDayOptionsTooltip>
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <Box
          component='div'
          sx={{
            display: mobile ? 'none' : 'flex',
            flexDirection: 'row',
            alignItems: 'end',
            gap: 1.5,
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={(_, tab) => setSelectedTab(tab)}
            sx={{ height: 'fit-content' }}
          >
            <Tab
              value='plan'
              label={
                <Typography sx={sharedStyles.tabLabel}>
                  {t('schedule.dialog.tab.plan')}
                </Typography>
              }
            />
            <Tab
              value='reserve'
              label={
                <Typography sx={sharedStyles.tabLabel}>
                  {t('schedule.dialog.tab.reserve')}
                </Typography>
              }
            />
          </Tabs>

          <ScheduleApprovalCard
            schedule={currentState}
            onChange={props.onModify}
          />
          <ScheduleApprovalRollbackCard
            schedule={currentState}
            onChange={props.onModify}
          />
        </Box>

        {mobile && (
          <Box sx={{ display: selectedTab === 'plan' ? 'block' : 'none' }}>
            <MobileSchedulePlan
              schedule={currentState}
              dates={dates}
              readOnly={readOnly || isExpired}
              statsLoading={statsLoading}
              onChange={(next) => {
                setCurrentState(next);
                refreshScheduleStats(next);
              }}
            />
          </Box>
        )}
        {mobile && (
          <Box sx={{ display: selectedTab === 'reserve' ? 'block' : 'none' }}>
            <MobileSchedulePlan
              reserve
              schedule={currentState}
              dates={dates}
              readOnly={readOnly || isExpired}
              statsLoading={false}
              onChange={setCurrentState}
            />
          </Box>
        )}
        {selectedTab === 'details' ? null : selectedTab === 'plan' ? (
          mobile ? null : (
            <TableContainer component={Paper} sx={sharedStyles.tableContainer}>
              <DataGrid
                sx={{
                  '& .gray1': {
                    backgroundColor: sharedColors.gray1,
                  },
                  '& .gray3': {
                    backgroundColor: sharedColors.gray3,
                  },
                  '& .gray4': {
                    backgroundColor: sharedColors.gray4,
                  },
                  '& .MuiDataGrid-columnHeader[data-field="name"]': {
                    position: 'sticky',
                    left: 0,
                    zIndex: 1002,
                    backgroundColor: 'white',
                  },
                  '& .sticky-column': {
                    position: 'sticky',
                    left: 0,
                    backgroundColor: 'white',
                    zIndex: 1000,
                  },
                }}
                disableVirtualization
                columns={columns}
                rows={rows}
                editMode='cell'
                hideFooter
                disableColumnMenu
                showCellVerticalBorder
                showColumnVerticalBorder
                isCellEditable={(params) =>
                  params.row.id !== t('shared.total') && !isExpired
                }
                processRowUpdate={onRowEditCommit}
                onProcessRowUpdateError={(err) => toastApiError(err)}
              />
            </TableContainer>
          )
        ) : mobile ? null : (
          <ReserveList
            schedule={currentState}
            onChange={setCurrentState}
            fourDPersonnelNames={
              props.clickedSchedule?.fourDPersonnelNames ?? []
            }
            dates={dates}
            readOnly={readOnly}
            isExpired={isExpired}
          />
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          ...(mobile
            ? {
                position: 'sticky',
                bottom: 0,
                bgcolor: 'background.paper',
                zIndex: 1100,
                borderTop: 1,
                borderColor: 'divider',
                pb: 'max(12px, var(--app-safe-area-bottom))',
              }
            : {}),
        }}
      >
        {mobile && (
          <Button onClick={(event) => setActionsAnchor(event.currentTarget)}>
            {t('schedule.mobile.actions')}
          </Button>
        )}
        <Popover
          open={mobile && !!actionsAnchor}
          anchorEl={actionsAnchor}
          onClose={() => setActionsAnchor(null)}
        >
          <Box
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              '& .MuiButton-root': {
                justifyContent: 'flex-start',
                textAlign: 'left',
              },
            }}
            onClick={() => setActionsAnchor(null)}
          >
            {!readOnly && (
              <Button color='error' onClick={() => setClickedDelete(true)}>
                {t('shared.delete')}
              </Button>
            )}
            {!readOnly && (
              <Button
                disabled={!currentState.isModified}
                onClick={() => setShowRestoreDialog(true)}
              >
                {t('schedule.restore.button_text')}
              </Button>
            )}
            {!isExpired && (
              <>
                <Button
                  disabled={!hasWriteAccess(tokenData, AccessLevel.DEPARTMENT)}
                  onClick={() => setShowEndEarlyDialog(true)}
                >
                  Planı Böl
                </Button>
                <Button
                  disabled={!hasWriteAccess(tokenData, AccessLevel.DEPARTMENT)}
                  onClick={() => setShowCombineScheduleDialog(true)}
                >
                  Plan Birleştir
                </Button>
              </>
            )}
          </Box>
        </Popover>
        <Box sx={{ display: mobile ? 'none' : 'contents' }}>
          {!readOnly && (
            <Button
              variant='contained'
              color='error'
              onClick={() => setClickedDelete(true)}
              sx={sharedStyles.buttonText}
            >
              {t('shared.delete')}
            </Button>
          )}
          <Box component='div' sx={{ display: 'flex', flexGrow: 1 }} />
          <Button
            variant='contained'
            color='inherit'
            onClick={props.onClose}
            sx={sharedStyles.buttonText}
          >
            {t('shared.cancel')}
          </Button>
          {!isExpired && (
            <Button
              variant='contained'
              color='primary'
              disabled={!hasWriteAccess(tokenData, AccessLevel.DEPARTMENT)}
              onClick={() => setShowEndEarlyDialog(true)}
              sx={sharedStyles.buttonText}
            >
              Planı Böl
            </Button>
          )}
          {!isExpired && (
            <Button
              variant='contained'
              color='primary'
              disabled={!hasWriteAccess(tokenData, AccessLevel.DEPARTMENT)}
              onClick={() => setShowCombineScheduleDialog(true)}
              sx={sharedStyles.buttonText}
            >
              Plan Birleştir
            </Button>
          )}
          {!readOnly && (
            <Button
              variant='contained'
              color='primary'
              disabled={!currentState.isModified}
              onClick={() => setShowRestoreDialog(true)}
              sx={sharedStyles.buttonText}
            >
              {t('schedule.restore.button_text')}
            </Button>
          )}
        </Box>
        {mobile && <Box sx={{ flexGrow: 1 }} />}
        {!isExpired && (
          <Button
            variant='contained'
            color='primary'
            disabled={
              isEmpty(currentState.name) ||
              readOnly ||
              (mobile && (!dirty || loading || statsLoading))
            }
            onClick={handleClickUpdate}
            sx={sharedStyles.buttonText}
          >
            {t('schedule.dialog.update')}
          </Button>
        )}
      </DialogActions>
    </ScheduleSurface>
  );
};

export default UpdateScheduleDialog;
